import {
  type CapabilityBlockReason,
  type ReportCause,
} from "@/types/agentcore";

import accessibilityRow from "./assets/accessibility-row.png";
import accessibilityToggle from "./assets/accessibility-toggle.png";
import privacyAndSecurity from "./assets/privacy-and-security.png";
import quitAndReopen from "./assets/quit-and-reopen.png";
import screenAndSystemAudioRecording from "./assets/screen-and-system-audio-recording.png";
import screenRecordingPermission from "./assets/screen-recording-permission.png";
import screenRecordingToggle from "./assets/screen-recording-toggle.png";

/**
 * One piece of a grant's walkthrough, in the order the hire meets it. A sequence rather than a list
 * of screenshots, because the two grants interleave their parts differently.
 */
export type WalkthroughPart =
  /** A numbered instruction, with the screenshot it refers to where one helps. */
  | {
      readonly kind: "step";
      readonly messageId: string;
      readonly image?: string;
      /** A macOS alert rather than a settings pane, which the design draws at about half the width. */
      readonly isDialogImage?: boolean;
    }
  /** The button that registers the grant and opens its pane. */
  | { readonly kind: "openAction" }
  /** The manual route through System Settings, for a deep link that did not open. */
  | { readonly kind: "manualRoute" }
  /** An unnumbered aside, which does not advance the count. */
  | { readonly kind: "note"; readonly messageId: string };

/** The one walkthrough part that carries a number, narrowed out of the union for the renderer. */
export type NumberedStep = Extract<WalkthroughPart, { kind: "step" }>;

/** One macOS grant, and everything the page needs to explain it. */
export interface PermissionStep {
  readonly reasonId: CapabilityBlockReason;
  readonly titleId: string;
  /** The System Settings pane name, for the "Open ..." button. Not the step title. */
  readonly paneNameId: string;
  /**
   * The row's own label in the System Settings side panel, for the manual route.
   *
   * Separate from `paneNameId` because macOS does not always agree with itself: the Screen Recording
   * pane is reached through a row labelled "Screen & System Audio Recording", and naming the button's
   * word there sends the hire looking for a row that is not in the list.
   */
  readonly paneRowNameId: string;
  /** Why the grant is needed, shown while it is outstanding. */
  readonly purposeId: string;
  /** Confirmation shown once macOS reports it held. */
  readonly grantedId: string;
  /** The pane's own row in System Settings, shown after Privacy & Security in the manual route. */
  readonly paneChip: string;
  /** The walkthrough in order. Words carry it; the screenshots beside them are decorative. */
  readonly walkthrough: readonly WalkthroughPart[];
}

/**
 * The two grants, in the order the design presents them.
 *
 * Accessibility leads to match the host, which lists it first in the reason ids it reports, so the
 * checklist and the pane the hire is actually sent to stay in step.
 */
export const PERMISSION_STEPS: readonly PermissionStep[] = [
  {
    grantedId: "macPermissions-accessibility-granted",
    paneChip: accessibilityRow,
    paneNameId: "macPermissions-accessibility-pane",
    paneRowNameId: "macPermissions-accessibility-pane",
    purposeId: "macPermissions-accessibility-purpose",
    reasonId: "accessibility-not-granted",
    titleId: "macPermissions-accessibility-title",
    walkthrough: [
      { kind: "step", messageId: "macPermissions-step-open" },
      { kind: "openAction" },
      { kind: "manualRoute" },
      {
        image: accessibilityToggle,
        kind: "step",
        messageId: "macPermissions-step-find-toggle",
      },
      { kind: "step", messageId: "macPermissions-step-come-back" },
      { kind: "note", messageId: "macPermissions-status-delay" },
    ],
  },
  {
    grantedId: "macPermissions-screen-recording-granted",
    paneChip: screenAndSystemAudioRecording,
    paneNameId: "macPermissions-screen-recording-pane",
    paneRowNameId: "macPermissions-screen-recording-row",
    purposeId: "macPermissions-screen-recording-purpose",
    reasonId: "screen-recording-not-granted",
    titleId: "macPermissions-screen-recording-title",
    walkthrough: [
      { kind: "step", messageId: "macPermissions-step-open" },
      { kind: "openAction" },
      {
        /* Opening this pane attempts a capture, and that attempt is what raises the dialog. */
        image: screenRecordingPermission,
        kind: "step",
        messageId: "macPermissions-screen-recording-step-dialog",
      },
      {
        image: screenRecordingToggle,
        kind: "step",
        messageId: "macPermissions-screen-recording-step-find-toggle",
      },
      { kind: "manualRoute" },
      {
        /* macOS's own dialog, so the hire recognises which buttons the step is talking about. */
        image: quitAndReopen,
        isDialogImage: true,
        kind: "step",
        messageId: "macPermissions-screen-recording-step-relaunch",
      },
      /*
       * Kept after the relaunch, as the design has it: the hire does come back to this window, just to
       * a fresh one, and the screen re-probes on that load and marks the step complete for them.
       */
      { kind: "step", messageId: "macPermissions-step-come-back" },
      { kind: "note", messageId: "macPermissions-status-delay" },
    ],
  },
];

/** Leads the manual route for both grants, since both panes live under Privacy & Security. */
export const PRIVACY_AND_SECURITY_CHIP = privacyAndSecurity;

/** Message id for a cause, kept beside the list so adding one cannot miss its copy. */
export const reportCauseMessageId = (cause: ReportCause): string =>
  `macPermissions-report-cause-${cause}`;
